import React from "react";

// icons
import AccountRemoveIcon from "mdi-material-ui/AccountRemove";
import AccountActivateIcon from "mdi-material-ui/AccountCheck";
import DeleteIcon from "@material-ui/icons/Delete";
import HistoryIcon from "@material-ui/icons/History";

//material ui
import makeStyles from "@material-ui/styles/makeStyles";
import Paper from "@material-ui/core/Paper";

import { PageHeader, SimpleShowLayout, TextField, FunctionField, PageFooter, BackButton, EditButton, translate, Button } from "jazasoft";

import ResetButton from "./ResetButton";

import { fieldOptions, getDistinctValues, isEmpty, listFromCsv } from "../../utils/helpers";
import ConfirmButton from "../../components/ConfirmButton";
import useUser from "./useUser";
import SettingKeys from "../../utils/SettingKeys";
import { useSetting } from "../../hooks";
import { UsersFields } from "../../utils/entityFields";
import UserHistoryDrawer from "./UserHistoryDrawer";

const Footer = ({ classes, onClick, authStateId, setOpenHistory, ...props }) => {
  const isNotLoggedinUser = authStateId !== props?.record?.id;
  const isUserEnabled = props?.record?.enabled;
  if (!props.record?.id) {
    return <div></div>;
  }
  return (
    <PageFooter className={classes.pageFooter}>
      <BackButton variant="outlined" />
      <EditButton variant="outlined" color="primary" {...props} />
      <ResetButton {...props} />
      {isNotLoggedinUser && isUserEnabled && (
        <ConfirmButton
          label="Deactivate"
          variant="outlined"
          color="primary"
          icon={<AccountRemoveIcon />}
          confirmTitle="Deactivate User?"
          onConfirm={_ => onClick("deactivate")}
        />
      )}
      {isNotLoggedinUser && !isUserEnabled && (
        <ConfirmButton
          label="Activate"
          variant="outlined"
          color="primary"
          icon={<AccountActivateIcon />}
          confirmTitle="Activate User?"
          onConfirm={_ => onClick("activate")}
        />
      )}
      {isNotLoggedinUser && (
        <ConfirmButton
          className={classes.colorRed}
          color="primary"
          label="Delete"
          variant="outlined"
          icon={<DeleteIcon />}
          confirmTitle="Delete User?"
          onConfirm={_ => onClick("delete")}
        />
      )}
      <Button variant="outlined" onClick={() => setOpenHistory(true)} label="History" >
        <HistoryIcon />
      </Button>
    </PageFooter >
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    margin: theme.spacing(3),
    padding: theme.spacing(3)
  },
  pageFooter: {
    "& > *": {
      marginRight: "1em"
    }
  },
  colorRed: {
    color: "#f44336"
  }
}));

const UserView = props => {
  const { basePath, resource, i18nKey, translate } = props;
  const [openHistory, setOpenHistory] = React.useState(false);

  const { user, iamUser = {}, appUser = {}, app, authState, allUnitList, onUpdate, onDelete } = useUser(props);
  const appId = (!isEmpty(app) && app.id) || null;
  const fields = useSetting(SettingKeys.ENTITY_FIELDS, "users");
  const roleList = getDistinctValues(iamUser?.roleList?.filter(r => r.appId === appId).map(r => r.role?.name));
  const record = {
    ...user,
    ...iamUser,
    unitIds: appUser?.unitIds,
    roleList: roleList?.join(",") || "",
    customers:
      iamUser?.permissionList
        ?.filter(p => p.app?.id === appId && p.key === "customerId")
        .map(p => p.name)
        .join(", ") || "",
    warehouses:
      iamUser?.permissionList
        ?.filter(p => p.app?.id === appId && p.key === "warehouseId")
        .map(p => p.name)
        .join(", ") || ""
  };

  const handleActionSubmit = action => {
    if (action === "activate" || action === "deactivate") {
      onUpdate({ action });
    } else {
      onDelete(props.id);
    }
  };

  const classes = useStyles();

  return (
    <div>
      {/* Header */}
      <PageHeader title={translate("resources.users.page.view")} />

      <UserHistoryDrawer
        open={openHistory}
        close={() => setOpenHistory(false)}
        id={props?.id}
      />

      {/* Content */}
      <Paper className={classes.container}>
        <SimpleShowLayout
          record={record}
          basePath={basePath}
          resource={resource}
          footer={
            <Footer
              classes={classes}
              record={record}
              authStateId={authState?.id}
              basePath={basePath}
              resource={resource}
              onClick={handleActionSubmit}
              setOpenHistory={setOpenHistory}
            />
          }
          i18nKey={i18nKey}>
          {fields.includes(UsersFields.FULL_NAME) && <TextField source="fullName" {...fieldOptions(3)} />}
          {fields.includes(UsersFields.USER_NAME) && <TextField source="username" {...fieldOptions(3)} />}
          <FunctionField
            source="unitIds"
            render={record =>
              isEmpty(record?.unitIds)
                ? ""
                : allUnitList
                    .filter(u => listFromCsv(record?.unitIds).includes(u.unitId))
                    .map(u => u.name)
                    .join(", ")
            }
          />
          {fields.includes(UsersFields.EMAIL) && <TextField source="email" {...fieldOptions(3)} />}
          {fields.includes(UsersFields.MOBILE) && <TextField source="mobile" {...fieldOptions(3)} />}
          {fields.includes(UsersFields.ROLES) && <TextField label="Roles" source="roleList" {...fieldOptions(3)} />}
          {fields.includes(UsersFields.CUSTOMER) && <TextField label="Customer Access" source="customers" {...fieldOptions(3)} />}
          {fields.includes(UsersFields.WAREHOUSES) && <TextField label="Warehouse Access" source="warehouses" {...fieldOptions(3)} />}
        </SimpleShowLayout>
      </Paper>
    </div>
  );
};

export default translate(UserView);
